import React from 'react';
import { isNotNull, isNull } from '../typeGuards';
import { FOCUSABLE_ELEMENTS, FOCUSABLE_ELEMENTS_WITHOUT_SKIPLINKS } from './constants';
import { windowLogger } from '../../_lib/Logger/windowLogger';
import { FocusTargetValueOrFalse } from 'focus-trap';

type Options = {
  dontFocusSkipLinks: boolean;
};

/**
 * Returns an Array of Element of a given HTMLElement
 */
export const getFocusableElements = (element: HTMLElement, options?: Options): HTMLElement[] => {
  const selector = (options?.dontFocusSkipLinks ? FOCUSABLE_ELEMENTS_WITHOUT_SKIPLINKS : FOCUSABLE_ELEMENTS).join(',');
  return Array.from(element.querySelectorAll(selector)).filter(
    el => !el.ariaDisabled || el.ariaDisabled === 'false'
  ) as HTMLElement[];
};

/**
 * Checks if any child element has focus.
 */
export const childHasFocus = <E extends HTMLElement | null>(element: React.RefObject<E> | null): boolean => {
  if (element && element.current) {
    const elements = getFocusableElements(element.current);
    for (let i = 0; i < elements.length; i++) {
      if (document.activeElement === elements[i]) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Checks if the last child element has focus.
 */
export const lastChildHasFocus = <E extends HTMLElement>(element: E): boolean => {
  const elements = getFocusableElements(element);
  if (elements.length) {
    const lastChild = elements[elements.length - 1];
    return document.activeElement === lastChild;
  }
  return false;
};

/**
 * Checks if the first child element has focus.
 */
export const firstChildHasFocus = <E extends HTMLElement>(element: E): boolean => {
  const elements = getFocusableElements(element);
  if (elements.length) {
    return document.activeElement === elements[0];
  }
  return false;
};

/**
 * Checks if an element has focusable children.
 */
export const hasFocusableChildren = <E extends HTMLElement>(element: E): boolean =>
  getFocusableElements(element).length > 0;

export const setElementFocus = (elem: HTMLElement | null, index: number, className: string): void => {
  if (elem && elem.children.length > 0) {
    if (elem.children[index].className === className) {
      const child = elem.children[index] as HTMLElement;
      child.focus();
    } else {
      const child = elem.children[index].querySelector(`.${className}`) as HTMLElement;
      if (child) {
        child.focus();
      }
    }
  }
};

/**
 * Finds the given `elementId` within the children of `elem`.
 *
 * @returns index of `elementId` or -1 if not found
 */
export const findElementIndex = (elem: HTMLElement, elementId: string): number => {
  for (let index = 0; index < elem.children.length; index += 1) {
    if (elem.children[index].id === elementId || elem.children[index].querySelector(`#${elementId}`)) {
      return index;
    }
  }
  return -1;
};

export const calculateNextElementsIndex = (elem: HTMLElement | null, elementId: string, direction: number): number => {
  if (isNull(elem)) {
    return 0;
  }
  const resultIndex = findElementIndex(elem, elementId);
  if (resultIndex === -1) {
    return 0;
  }
  return (resultIndex + elem.children.length + direction) % elem.children.length;
};

export const setElementFocusById = (elem: HTMLElement | null, elementId: string): void => {
  if (isNotNull(elem)) {
    for (let index = 0; index < elem.children.length; index += 1) {
      const child = elem.children[index] as HTMLElement;
      if (child.id === elementId) {
        child.focus();
        break;
      }
    }
  }
};

/**
 * Checks whether the given Ref's element is the activeElement.
 */
export const isActiveElement = <E extends HTMLElement | null>(ref: React.RefObject<E>): boolean =>
  document.activeElement === ref.current;

/**
 * Checks whether the given Ref's element contains the EventTarget element.
 */
export const containsTarget = <E extends HTMLElement | null>(
  ref: React.RefObject<E | null>,
  target: EventTarget | null
): boolean =>
  // see https://stackoverflow.com/a/54351811
  ref.current && target ? ref.current.contains(target as Element) : false;

/**
 * Checks whether the given Ref's element contains or _is_ the EventTarget element.
 */
export const isOrContainsTarget = <E extends HTMLElement | null>(
  ref: React.RefObject<E>,
  target: EventTarget | null
): boolean => (ref.current && ref.current === target) || containsTarget(ref, target);

/**
 * Checks whether the clicked element toggle collapsible button is.
 */
export const isToggleCollapsibleButton = <E extends HTMLElement | null>(
  ref: React.RefObject<E>,
  event: Event | null
): boolean =>
  (ref.current || false) &&
  (ref.current?.parentNode === (event?.target as HTMLElement)?.parentNode ||
    ref.current?.parentNode === (event?.target as HTMLElement)?.parentNode?.parentNode);

export function focusNonFocusableElement(
  element: HTMLElement,
  options: { onFocus?: (element: HTMLElement) => void; onBlur?: (element: HTMLElement) => void } = {}
) {
  element.addEventListener(
    'blur',
    event => {
      (event.target as HTMLElement)?.removeAttribute('tabindex');
      options.onBlur?.(event.target as HTMLElement);
    },
    { once: true }
  );
  element.setAttribute('tabindex', '-1');
  options.onFocus?.(element);
  element.focus();
}

/**
 * Focus an element based on its id attribute.
 *  We set tabindex to -1, so we can focus that element and remove the tabindex
 *  attribute onBlur.
 */
export const focusElementWithId = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    focusNonFocusableElement(element);
  }
};

/**
 * Focusing non focusable element by
 *  - setting tabindex -1 to programatically focus it
 *  - toggle a class to make focus visible
 *  - add event listener to remove class and tabindex on blur
 */
export const focusNonFocusableElementAndToggleClassName = (
  element: HTMLElement,
  elementForClassToggle: HTMLElement | Element,
  classToToggle: string
) => {
  focusNonFocusableElement(element, {
    onBlur: () => elementForClassToggle?.classList.remove(classToToggle),
    onFocus: () => elementForClassToggle?.classList.add(classToToggle),
  });
};

/**
 * To be used for ModalDialog focusOnReturn prop, which uses a FocusTrap.
 * Memorizes previous and next element of possibly already deleted currently selected element.
 * Provides correct element to be focused next.
 */
export const getFocusOnReturnFunction = (currentElement?: HTMLElement | null, fallbackElement?: HTMLElement | null) => {
  if (currentElement) {
    const previousElement = currentElement.previousElementSibling as HTMLElement;
    const nextElement = currentElement.nextElementSibling as HTMLElement;

    return (prevFocus: HTMLElement | SVGElement): FocusTargetValueOrFalse => {
      // will be evaluated after view is refreshed
      const candidates = [prevFocus, nextElement, previousElement, fallbackElement];
      return candidates.find(el => el && document.contains(el)) || false;
    };
  }

  return undefined;
};

/**
 * Loads a stylesheet by appending a <link ... /> to the head.
 */
export const addStyleSheet = ({ href }: { href: string }) => {
  const allLinksInHead: HTMLLinkElement[] = Array.from(document.head.getElementsByTagName('link'));
  if (allLinksInHead.some(link => link.getAttribute('href') === href)) {
    return;
  }

  try {
    const linkEl = document.createElement('link');
    linkEl.setAttribute('rel', 'stylesheet');
    linkEl.setAttribute('media', 'all');
    linkEl.setAttribute('href', href);
    document.head.append(linkEl);
  } catch (e) {
    windowLogger.error(e);
  }
};
